import Papa from 'papaparse';
import { phoneValidator } from '../../../../utils/validation';
import {
  makeNewRow,
  RowModel,
  SimpleRole,
  SimpleRoleMapping,
} from './controller';

// Use a more permissive phone number regex during CSV parsing. This is helpful since people
// may have limited control over how they are building a CSV, and we want to avoid them having
// to make manual edits across a large number of rows
const separator = `[\\s.-]?`;
// optional country code. Use 2 groups so that we capture only the digits, but the entire
// + and digits portion is matched as a whole
const countryCode = `(?:\\+?(\\d{1,2}))?${separator}`;
// area code can optionally be surrounded by ()
const areaCode = `(\\(?\\d{3}\\)?)${separator}`;
const rest = `(\\d{3})${separator}(\\d{4})`;
const phoneRegex = new RegExp(`^${countryCode}${areaCode}${rest}$`);

function normalizePhoneNumber(phone: string) {
  if (phoneValidator.value.test(phone)) {
    return phone;
  }

  const match = phone.match(phoneRegex);
  if (!match) {
    return phone;
  }

  const hasCountryCode = match[1] !== undefined;
  const formattedCountryCode = hasCountryCode ? `+${match[1]} ` : '';

  return `${formattedCountryCode}${match[2]}-${match[3]}-${match[4]}`;
}

interface CsvRow {
  'first name': string;
  'last name': string;
  email: string;
  phone: string;
  'crm user id (optional)': string;
}

export const maxFileSize = 500 * 1024;

export const headerFields: (keyof CsvRow)[] = [
  'first name',
  'last name',
  'email',
  'phone',
  'crm user id (optional)',
];

export const errorMessages = {
  invalidHeader:
    'File contained unexpected headers. Please make sure the file you upload matches the sample CSV file.',
  fileTooLarge:
    'File size is too large. Maximum file size is 500KB. Please try again.',
  problemUploading:
    'There was a problem uploading your file. Please try again.',
};

interface ParseError {
  parseError: string;
}
function csvRowToGridRow(row: CsvRow): RowModel | ParseError {
  const normalizedPhone = normalizePhoneNumber(row.phone.trim());

  return makeNewRow({
    firstName: row['first name'],
    lastName: row['last name'],
    email: row.email,
    phone: normalizedPhone,
    crmUserId: row['crm user id (optional)'],
    roles: SimpleRoleMapping[SimpleRole.SALESPERSON],
  });
}

export function isParseError(obj: any): obj is ParseError {
  return typeof obj === 'object' && typeof obj.parseError === 'string';
}

function isValidRow(row: any): row is CsvRow {
  return (
    typeof row === 'object' &&
    headerFields.every((f) => f in row) &&
    Object.keys(row).length === headerFields.length
  );
}

function parseRow(
  data: Record<string, string>,
  errors: Papa.ParseError[],
): RowModel | ParseError {
  if (errors.length > 0) {
    return {
      parseError: errors.map((e) => e.message).join('\n'),
    };
  }

  if (!isValidRow(data)) {
    return {
      parseError: errorMessages.invalidHeader,
    };
  }
  return csvRowToGridRow(data);
}

export async function handleCsv(
  file: File | null,
): Promise<RowModel[] | ParseError> {
  if (!file) {
    return [];
  }

  if (file.size > maxFileSize) {
    return {
      parseError: errorMessages.fileTooLarge,
    };
  }

  const gridRows: RowModel[] = [];

  return new Promise((resolve) => {
    Papa.parse<Record<string, string>, File>(file, {
      skipEmptyLines: true,
      header: true,
      step(results, parser) {
        const parsed = parseRow(results.data, results.errors);
        if (isParseError(parsed)) {
          resolve(parsed);
          parser.abort();
          return;
        }

        gridRows.push(parsed);
      },
      complete() {
        resolve(gridRows);
      },
      error() {
        resolve({
          parseError: errorMessages.problemUploading,
        });
      },
    });
  });
}

const sampleCsvContent = headerFields.join(',');
const sampleCsvFile = new File([sampleCsvContent], 'users-sample.csv', {
  type: 'text/csv',
});
export const sampleCsvUrl = window.URL.createObjectURL(sampleCsvFile);
